import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Chip,
  TextField,
  Alert,
  Snackbar,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DataTable, { Column } from '../../components/common/DataTable';
import SearchBar from '../../components/common/SearchBar';
import StatusChip from '../../components/common/StatusChip';
import { getApkAnalyses, createBlacklist } from '../../services/api/securityApi';
import { ApkAnalysis, AnalysisFilters, BlacklistFormData } from '../../types/analysis';
import { formatDateTime } from '../../utils/formatters';

const ApkAnalysisPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<AnalysisFilters>({
    search: '',
    page: 0,
    limit: 20,
  });
  const [searchInput, setSearchInput] = useState('');
  const [selectedAnalysis, setSelectedAnalysis] = useState<ApkAnalysis | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [blacklistDialogOpen, setBlacklistDialogOpen] = useState(false);
  const [blacklistForm, setBlacklistForm] = useState<BlacklistFormData>({
    packageName: '',
    sha256: '',
    appName: '',
    threatType: 'malware',
    riskLevel: 'high',
    description: '',
    source: 'manual',
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['apkAnalyses', filters],
    queryFn: () => getApkAnalyses(filters),
  });

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, search: searchInput, page: 0 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleRowsPerPageChange = (limit: number) => {
    setFilters(prev => ({ ...prev, limit, page: 0 }));
  };

  const handleRiskLevelFilterChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      riskLevel: value === 'all' ? undefined : (value as 'HIGH' | 'MEDIUM' | 'LOW' | 'SAFE'),
      page: 0,
    }));
  };

  const handleRowClick = (analysis: ApkAnalysis) => {
    setSelectedAnalysis(analysis);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedAnalysis(null);
  };

  const handleOpenBlacklistDialog = (analysis: ApkAnalysis) => {
    setBlacklistForm({
      packageName: '',
      sha256: analysis.sha256,
      appName: analysis.filename.replace('.apk', ''),
      threatType: 'malware',
      riskLevel: analysis.riskEvaluation.riskLevel.toLowerCase() as 'low' | 'medium' | 'high' | 'critical',
      description: `APK 분석 결과: 위험도 ${analysis.riskEvaluation.riskLevel}, 위험 요소 ${analysis.riskEvaluation.dangerCount}개 발견`,
      source: 'apk_analysis',
    });
    setBlacklistDialogOpen(true);
  };

  const handleCloseBlacklistDialog = () => {
    setBlacklistDialogOpen(false);
  };

  const addToBlacklistMutation = useMutation({
    mutationFn: createBlacklist,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blacklist'] });
      setSnackbar({
        open: true,
        message: '블랙리스트에 추가되었습니다.',
        severity: 'success',
      });
      handleCloseBlacklistDialog();
      handleCloseDetailDialog();
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '블랙리스트 추가에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const handleAddToBlacklist = () => {
    addToBlacklistMutation.mutate(blacklistForm);
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const columns: Column<ApkAnalysis>[] = [
    {
      id: 'filename',
      label: '파일명',
      minWidth: 200,
    },
    {
      id: 'sha256',
      label: 'SHA256',
      minWidth: 150,
      format: (value: string) => value.substring(0, 16) + '...',
    },
    {
      id: 'riskEvaluation',
      label: '위험도',
      minWidth: 100,
      align: 'center',
      format: (_value: any, row: ApkAnalysis) => (
        <StatusChip
          status={row.riskEvaluation.riskLevel}
          label={row.riskEvaluation.riskLevel}
        />
      ),
    },
    {
      id: 'dangerCount',
      label: '위험 요소',
      minWidth: 100,
      align: 'center',
      format: (_value: any, row: ApkAnalysis) => row.riskEvaluation.dangerCount,
    },
    {
      id: 'createdAt',
      label: '분석일',
      minWidth: 150,
      format: (value: Date) => formatDateTime(value),
      sortable: true,
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        APK 분석 관리
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ flex: 1 }}>
            <SearchBar
              value={searchInput}
              onChange={setSearchInput}
              onSearch={handleSearch}
              placeholder="SHA256 또는 파일명으로 검색"
              fullWidth
            />
          </Box>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>위험도 필터</InputLabel>
            <Select
              value={filters.riskLevel || 'all'}
              label="위험도 필터"
              onChange={e => handleRiskLevelFilterChange(e.target.value)}
              size="small"
            >
              <MenuItem value="all">전체</MenuItem>
              <MenuItem value="HIGH">HIGH</MenuItem>
              <MenuItem value="MEDIUM">MEDIUM</MenuItem>
              <MenuItem value="LOW">LOW</MenuItem>
              <MenuItem value="SAFE">SAFE</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <DataTable
        columns={columns}
        data={(data?.items || []).map(item => ({ ...item, id: item._id }))}
        total={data?.total || 0}
        page={filters.page}
        rowsPerPage={filters.limit}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onRowClick={(row: any) => handleRowClick(row as ApkAnalysis)}
        loading={isLoading}
      />

      {/* 상세 정보 모달 */}
      <Dialog open={detailDialogOpen} onClose={handleCloseDetailDialog} maxWidth="md" fullWidth>
        <DialogTitle>APK 분석 상세 정보</DialogTitle>
        <DialogContent>
          {selectedAnalysis && (
            <Box sx={{ pt: 2 }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      파일명
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedAnalysis.filename}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      위험도
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <StatusChip
                        status={selectedAnalysis.riskEvaluation.riskLevel}
                        label={selectedAnalysis.riskEvaluation.riskLevel}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    SHA256
                  </Typography>
                  <Typography
                    variant="body2"
                    gutterBottom
                    sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}
                  >
                    {selectedAnalysis.sha256}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      위험 요소 개수
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedAnalysis.riskEvaluation.dangerCount}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      분석일
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formatDateTime(selectedAnalysis.createdAt)}
                    </Typography>
                  </Box>
                </Box>
                {selectedAnalysis.riskEvaluation.dangers &&
                  Object.keys(selectedAnalysis.riskEvaluation.dangers).length > 0 && (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          위험 요소 상세
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {Object.keys(selectedAnalysis.riskEvaluation.dangers).map(danger => (
                            <Chip key={danger} label={danger} color="warning" size="small" />
                          ))}
                        </Stack>
                      </Box>
                    </>
                  )}
                {selectedAnalysis.result && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        원본 분석 데이터
                      </Typography>
                      <Paper
                        sx={{
                          p: 2,
                          bgcolor: 'grey.100',
                          maxHeight: 300,
                          overflow: 'auto',
                        }}
                      >
                        <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                          {JSON.stringify(selectedAnalysis.result, null, 2)}
                        </pre>
                      </Paper>
                    </Box>
                  </>
                )}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => selectedAnalysis && handleOpenBlacklistDialog(selectedAnalysis)}
            variant="contained"
            color="error"
          >
            블랙리스트에 추가
          </Button>
          <Button onClick={handleCloseDetailDialog}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 블랙리스트 추가 모달 */}
      <Dialog open={blacklistDialogOpen} onClose={handleCloseBlacklistDialog} maxWidth="sm" fullWidth>
        <DialogTitle>블랙리스트에 추가</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="패키지명 (선택사항)"
                value={blacklistForm.packageName}
                onChange={e =>
                  setBlacklistForm(prev => ({ ...prev, packageName: e.target.value }))
                }
                fullWidth
                placeholder="com.example.app"
                helperText="패키지명을 알고 있다면 입력하세요"
              />
              <TextField
                label="SHA256"
                value={blacklistForm.sha256}
                onChange={e => setBlacklistForm(prev => ({ ...prev, sha256: e.target.value }))}
                fullWidth
                required
                disabled
                helperText="APK 파일의 SHA256 해시"
              />
              <TextField
                label="앱 이름"
                value={blacklistForm.appName}
                onChange={e => setBlacklistForm(prev => ({ ...prev, appName: e.target.value }))}
                fullWidth
                required
              />
              <FormControl fullWidth required>
                <InputLabel>위협 유형</InputLabel>
                <Select
                  value={blacklistForm.threatType}
                  label="위협 유형"
                  onChange={e =>
                    setBlacklistForm(prev => ({ ...prev, threatType: e.target.value }))
                  }
                >
                  <MenuItem value="malware">Malware (악성코드)</MenuItem>
                  <MenuItem value="trojan">Trojan (트로이목마)</MenuItem>
                  <MenuItem value="spyware">Spyware (스파이웨어)</MenuItem>
                  <MenuItem value="adware">Adware (애드웨어)</MenuItem>
                  <MenuItem value="ransomware">Ransomware (랜섬웨어)</MenuItem>
                  <MenuItem value="phishing">Phishing (피싱)</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>위험도</InputLabel>
                <Select
                  value={blacklistForm.riskLevel}
                  label="위험도"
                  onChange={e =>
                    setBlacklistForm(prev => ({
                      ...prev,
                      riskLevel: e.target.value as 'low' | 'medium' | 'high' | 'critical',
                    }))
                  }
                >
                  <MenuItem value="low">Low (낮음)</MenuItem>
                  <MenuItem value="medium">Medium (중간)</MenuItem>
                  <MenuItem value="high">High (높음)</MenuItem>
                  <MenuItem value="critical">Critical (매우 높음)</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="설명"
                value={blacklistForm.description}
                onChange={e =>
                  setBlacklistForm(prev => ({ ...prev, description: e.target.value }))
                }
                fullWidth
                multiline
                rows={3}
                placeholder="위협에 대한 상세 설명을 입력하세요"
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBlacklistDialog}>취소</Button>
          <Button
            onClick={handleAddToBlacklist}
            variant="contained"
            color="error"
            disabled={addToBlacklistMutation.isPending}
          >
            {addToBlacklistMutation.isPending ? '추가 중...' : '블랙리스트에 추가'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ApkAnalysisPage;
